import axios from "axios"
import { toast } from "react-toastify"
import React from "react"
import { v4 as uuid } from "uuid"
import { t } from "i18next"
import { ModalContextProps } from "@/components/ModalProvider/Modals"
import { TRANSLATION_LIMIT_MESSAGE_KEY } from "@/utils/brand"

export const languageMap: Record<string, string> = {
    de: "German",
    fr: "French",
    es: "Spanish",
    it: "Italian",
    sv: "Swedish",
    en: "English",
    ja: "Japanese",
    ar: "Arabic",
}

interface TranslateParams {
    id: string | undefined
    currentPage: React.MutableRefObject<number>
    setIsTranslating: React.Dispatch<React.SetStateAction<boolean>>
    currentLanguage: string
    setFileUrl: React.Dispatch<React.SetStateAction<string>>
    originalFileUrl: string
    setFileNameCurrent: React.Dispatch<React.SetStateAction<string>>
    fileName: string
    setCurrentLanguage: React.Dispatch<React.SetStateAction<string>>
    setPage: React.Dispatch<React.SetStateAction<number>>
    originalPage: React.MutableRefObject<number>
    setIsFullFileTranslated: React.Dispatch<React.SetStateAction<boolean>>
    totalPages: number
    targetLanguageCode: string
    confirm: (message: string, props: ModalContextProps) => void
    isConfirmingFullTranslation: React.MutableRefObject<boolean>
    forceLanguageTranslation: React.MutableRefObject<boolean>
}

export function handleTranslatePDF(params: TranslateParams) {
    const targetLanguageReadable = languageMap[params.targetLanguageCode] || "Your language"
    const { confirm, isConfirmingFullTranslation, forceLanguageTranslation } = params

    const resetToOriginal = () => {
        console.log("resetToOriginal")
        const {
            setFileUrl,
            originalFileUrl,
            setFileNameCurrent,
            fileName,
            setIsTranslating,
            setCurrentLanguage,
            setPage,
            originalPage,
            setIsFullFileTranslated,
        } = params
        setFileUrl(originalFileUrl)
        setFileNameCurrent(fileName)
        setIsTranslating(false)
        setCurrentLanguage("original")
        setPage(originalPage.current)
        setIsFullFileTranslated(false)
    }

    const getEndpoint = (action: "page" | "full", targetLanguageCode: string, translateSourceFile: boolean) => {
        const { id, currentPage, totalPages } = params
        const pageToTranslate = action === "page" ? currentPage.current + 1 : undefined

        let endpoint =
            action === "page"
                ? `/files/download_translated_page/${id}/${pageToTranslate}/${totalPages}/${targetLanguageCode}`
                : `/files/download_translated/${id}/${totalPages}/${targetLanguageCode}`

        if (translateSourceFile) {
            endpoint += "?download_source=true&return_pdf=true"
        }

        return endpoint
    }

    const fetchTranslation = async (
        action: "page" | "full",
        targetLanguageCode: string,
        translateSourceFile: boolean
    ) => {
        const endpoint = getEndpoint(action, targetLanguageCode, translateSourceFile)
        return await axios.get(endpoint, {
            responseType: "arraybuffer",
            params: {
                force_translate: forceLanguageTranslation.current,
                force_translate_full: isConfirmingFullTranslation.current,
            },
            timeout: action === "page" ? 120000 : 300000,
        })
    }

    const handleSuccessfulTranslation = (data: ArrayBuffer, action: "page" | "full", targetLanguageCode: string) => {
        const {
            setPage,
            setFileUrl,
            setFileNameCurrent,
            fileName,
            setCurrentLanguage,
            setIsFullFileTranslated,
            currentPage,
        } = params
        const blob = new Blob([data], { type: "application/pdf" })
        const pageToTranslate = action === "page" ? currentPage.current + 1 : undefined
        setPage(0)
        setFileUrl(URL.createObjectURL(blob))
        setFileNameCurrent(
            action === "page"
                ? `${fileName.replace(".pdf", "")}_page_${pageToTranslate}_${targetLanguageCode}.pdf`
                : `${fileName.replace(".pdf", "")}_${targetLanguageCode}.pdf`
        )
        setCurrentLanguage("translated")
        setIsFullFileTranslated(action === "full")
    }

    const handleTranslationError = (error: any) => {
        if (error?.response?.status === 402) {
            isConfirmingFullTranslation.current = false
            forceLanguageTranslation.current = false
            toast.error(
                t(TRANSLATION_LIMIT_MESSAGE_KEY),
                {
                    toastId: uuid(),
                    autoClose: false,
                    closeOnClick: false,
                }
            )
        } else {
            isConfirmingFullTranslation.current = false
            forceLanguageTranslation.current = false
            toast.error(t("Translation failed: {{error}}", { error: error?.message }))
        }
    }

    const handleFullFileTranslationConfirmation = (targetLanguageCode: string, translateSourceFile: boolean) => {
        return new Promise<void>(resolve => {
            confirm(t("Are you sure you want to translate all {{num}} pages?", { num: params.totalPages }), {
                onOk: () => {
                    isConfirmingFullTranslation.current = true
                    handleTranslate("full", targetLanguageCode, translateSourceFile)
                    resolve()
                },
                onCancel: () => {
                    isConfirmingFullTranslation.current = false
                    forceLanguageTranslation.current = false
                    params.setIsTranslating(false)
                },
            })
        })
    }
    const handleSameLanguageTranslation = (
        action: "page" | "full",
        targetLanguageCode: string,
        translateSourceFile: boolean
    ) => {
        return new Promise<void>(resolve => {
            confirm(
                t(
                    "This file is already in {{targetLanguageReadable}}. Do you still want to proceed with the translation?",
                    { targetLanguageReadable }
                ),
                {
                    onOk: () => {
                        forceLanguageTranslation.current = true
                        handleTranslate(action, targetLanguageCode, translateSourceFile)
                        resolve()
                    },
                    onCancel: () => {
                        isConfirmingFullTranslation.current = false
                        forceLanguageTranslation.current = false
                        params.setIsTranslating(false)
                    },
                }
            )
        })
    }
    const handleTranslate = async (
        action: "page" | "full",
        targetLanguageCode: string,
        translateSourceFile: boolean
    ) => {
        const { id, currentPage, setIsTranslating, currentLanguage, originalPage } = params

        if (!id || (action === "page" && currentPage.current === undefined)) {
            console.error(`Invalid ID or page number for translation: ${id}, ${currentPage.current}`)
            return
        }

        setIsTranslating(true)

        if (currentLanguage !== "original") {
            resetToOriginal()
            return
        }

        originalPage.current = currentPage.current

        try {
            if (targetLanguageCode === undefined) {
                throw new Error(t("Target language code is undefined"))
            }

            const response = await fetchTranslation(action, targetLanguageCode, translateSourceFile)
            const contentType = response.headers["content-type"]
            if (contentType && contentType.includes("application/json")) {
                const decoder = new TextDecoder("utf-8")
                const jsonString = decoder.decode(response.data)
                const jsonData = JSON.parse(jsonString)
                if (response.status === 200 && jsonData?.detail === "Are you sure you want to translate all pages?") {
                    setIsTranslating(true)
                    await handleFullFileTranslationConfirmation(targetLanguageCode, translateSourceFile)
                    return
                }
                if (response.status === 200 && jsonData?.detail === "File is already in the target language") {
                    setIsTranslating(true)
                    await handleSameLanguageTranslation(action, targetLanguageCode, translateSourceFile)
                    return
                }
            }

            if (response.status !== 200) {
                throw new Error(t("Failed to fetch translated file"))
            }
            const asArrayBuffer = response.data as ArrayBuffer
            handleSuccessfulTranslation(asArrayBuffer, action, targetLanguageCode)
            setIsTranslating(false)
            isConfirmingFullTranslation.current = false
            forceLanguageTranslation.current = false
        } catch (error: any) {
            handleTranslationError(error)
            setIsTranslating(false)
        }
    }

    return handleTranslate
}
